import { makeApiRequest } from '@/src/lib/api-request'
import { requestV3 } from '@/src/lib/customAxios'
import { useMutation } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'

export interface VideoDetails {
  fileToken: string
  title: string
  description: string
  thumbnailUrl: File | string
  categoryIds: string
  languageIds: string
  uploadThumbnail: boolean
  visibility: '0' | '1' | '2'
  schedule_status: '0' | '1'
  schedule_date: string
  schedule_time: string
  subtitlesRequested: '0' | '1'
  isMadeForKids?: string
  isRestrictAge?: string
  catalog_title?: string
  catalog_description?: string
  ppv_type?: string // 0 = single video, 1 = playlist
  catalog_global_price?: number // USD amount
  catalog_india_price?: number // INR amount
  catalog_id?: number // Catalog ID for playlist, if applicable
  video_id?: string // Video ID for existing videos, if applicable
  video_type?: '0' | '1' | '2' // '0' = free, '1' = ppv, '2' = password protected
  password?: string // Only required if video_type = '2'
  original_publish_date?: string
  extra_amounts?: {
    country_code: string
    amount: number
  }[]
}
export interface EditVideoDetails {
  title: string
  description: string
  thumbnailUrl: File | string
  categoryId: Number[]
  languageId: Number[]
  uploadThumbnail?: boolean
  isMadeForKids: string
  isRestrictAge?: string
  eventId: Number
  original_publish_date?: string
}

export const useSaveVideoDetailsMutation = ({
  draftMode,
}: {
  draftMode: boolean
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['video', 'save'],
    mutationFn: async (
      data: VideoDetails,
      customCallback?: (customImgURL: string) => void
    ) => {
      if (data.uploadThumbnail) {
        console.log('uploading image')
        const formData = new FormData()
        formData.append('file', data.thumbnailUrl)
        try {
          console.log('try block')
          const response = await requestV3({
            url: '/ImageUpload/uploadThumbnail',
            method: 'POST',
            data: formData,
          })
          if (!response.data.fullUrl) {
            throw new Error('Failed to upload image')
          }
          console.log(response)
          data.thumbnailUrl = response.data.fullUrl
          customCallback && customCallback(response.data.fullUrl)
        } catch (e) {
          throw new Error('Failed to upload image')
        }
      }

      await makeApiRequest({
        url: '/Video_Upload/saveVideoDetailsById',
        method: 'POST',
        data: data,
        isAuthenticated: true,
        fallbackError: 'Failed to save video details',
        isV3Api: true
      })
      return data
    },
    onSuccess: () => {
      if (draftMode) {
        queryClient.invalidateQueries({ queryKey: ['draft-videos'] })
        queryClient.invalidateQueries({ queryKey: ['scheduled-videos'] })
        queryClient.invalidateQueries({ queryKey: ['all-video-count'] })
      }
    },
  })
}

export const useEditVideoDetailsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['video', 'edit'],
    mutationFn: async (
      data: EditVideoDetails,
      customCallback?: (customImgURL: string) => void
    ) => {
      if (data.uploadThumbnail) {
        const formData = new FormData()
        formData.append('file', data.thumbnailUrl)
        try {
          const response = await requestV3({
            url: '/ImageUpload/uploadThumbnail',
            method: 'POST',
            data: formData,
          })
          if (!response.data.fullUrl) {
            throw new Error('Failed to upload image')
          }
          data.thumbnailUrl = response.data.fullUrl
          customCallback && customCallback(response.data.fullUrl)
        } catch (e) {
          throw new Error('Failed to upload image')
        }
      }
      delete data.uploadThumbnail;
      await makeApiRequest({
        url: `/Videos/EditPublishedVideo`,
        method: 'PATCH',
        data: data,
        isAuthenticated: true,
        fallbackError: 'Failed to save video details',
        isV3Api: true
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-videos'] })
    },
  })
}
